'use client'

import { Link, usePathname, useRouter } from '@/i18n/navigation'
import { cn } from '@/lib/utils'
import { useLocale } from 'next-intl'
import Image from 'next/image'
import { Select } from '../ui/select'

type Language = {
  code: string
  label: string
}

type LanguageSwitcherItemProps = {
  label: string
  value: string
  className?: string
}

const LANGUAGES: Language[] = [
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'en', label: 'English' },
]

const LanguageSwitcherItem = ({
  label,
  value,
  className,
}: LanguageSwitcherItemProps) => (
  <span className={cn('flex items-center gap-2 font-medium', className)}>
    <Image
      src={value === 'en' ? '/icons/flag-us.png' : '/icons/flag-vn.png'}
      alt={value}
      width={16}
      height={16}
      className='h-4 w-4 rounded-full object-cover'
    />
    {label}
  </span>
)

export default function LanguageSwitcher() {
  const pathname = usePathname()
  const currentLocaleInServer = useLocale()
  const router = useRouter()

  const getBasePath = () => {
    const segments = pathname.split('/')
    if (LANGUAGES.some((l) => l.code === segments[1])) {
      const rest = segments.slice(2).join('/')
      return rest ? `/${rest}` : '/'
    }
    return pathname
  }

  const basePath = getBasePath()

  const handleLanguageChange = (lang: string) => {
    if (lang === currentLocaleInServer) return
    router.replace(basePath, { locale: lang })
  }

  const languageOptions = LANGUAGES.filter(
    (lang) => lang.code !== currentLocaleInServer
  ).map((lang) => ({
    value: lang.code,
    label: (
      <Link
        href={basePath}
        locale={lang.code}
        aria-label={`Chuyển sang ${lang.label}`}
        onClick={(e) => {
          if (currentLocaleInServer === lang.code) {
            e.preventDefault()
          }
        }}
        className='flex items-center gap-1'
      >
        <LanguageSwitcherItem
          label={lang.label}
          value={lang.code}
          className='text-neutral-700'
        />
        <span className='text-neutral-700'>
          ({lang.code === 'vi' ? 'VN' : 'US'})
        </span>
      </Link>
    ),
  }))

  return (
    <Select
      options={languageOptions}
      value={currentLocaleInServer}
      onValueChange={handleLanguageChange}
      renderValue={() => (
        <LanguageSwitcherItem
          label={currentLocaleInServer === 'vi' ? 'VN' : 'US'}
          value={currentLocaleInServer}
        />
      )}
      className={{
        trigger:
          'h-fit border-none bg-light/70 px-3 py-1.5 text-neutral-700 hover:bg-transparent lg:!bg-transparent lg:p-0 lg:px-2 lg:text-neutral-200 [&_.icon-down]:lg:text-neutral-200',
        content: 'z-[105] bg-neutral-100',
      }}
    />
  )
}
